import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild
} from '@angular/core';

interface Hotspot {
  nx: number;
  ny: number;
  advocateIdx: number;
  expandProgress: number;
  targetExpand: number;
  label: string | null;
  labelOpacity: number;
  labelOffsetY: number;
}

export interface AdvocateTooltip {
  name: string;
  role: string;
  shares: number;
  regs: number;
}

interface TooltipView {
  leftPx: number;
  topPx: number;
  advocate: AdvocateTooltip;
}

const ASSET = (name: string) => `assets/images/${name}`;

const HEADSHOT_SRCS = [
  ASSET('sarah.jpg'),
  ASSET('james.jpg'),
  ASSET('maria.jpg'),
  ASSET('alex.jpg'),
  ASSET('chen.jpg'),
  ASSET('priya.jpg'),
  ASSET('tom.jpg'),
  ASSET('lena.jpg')
];

const ADVOCATE_NAMES: AdvocateTooltip[] = [
  { name: 'Sarah', role: 'Speaker', shares: 12, regs: 9 },
  { name: 'James', role: 'Exhibitor', shares: 8, regs: 5 },
  { name: 'Maria', role: 'Attendee', shares: 15, regs: 11 },
  { name: 'Alex', role: 'Speaker', shares: 6, regs: 4 },
  { name: 'Chen', role: 'Exhibitor', shares: 10, regs: 7 },
  { name: 'Priya', role: 'Speaker', shares: 9, regs: 6 },
  { name: 'Tom', role: 'Attendee', shares: 14, regs: 10 },
  { name: 'Lena', role: 'Exhibitor', shares: 7, regs: 5 }
];

const SHARING_LABELS = [
  '+3 registrations',
  'Shared via LinkedIn',
  'WhatsApp',
  '+12 clicks',
  '+7 registrations',
  'Shared via Email',
  'Slack',
  '+5 sign-ups'
];

const AVATAR_COLORS = ['#3980C2', '#68C5F0', '#A94D9D', '#363795', '#5BA8D9', '#7B61C2'];

const CYCLE_MS = 5000;
const FADE_START_MS = 3500;

@Component({
  selector: 'app-login-network-visualization',
  templateUrl: './login-network-visualization.component.html',
  styleUrls: ['./login-network-visualization.component.css']
})
export class LoginNetworkVisualizationComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('container', { static: false }) containerRef!: ElementRef<HTMLDivElement>;

  tooltip: TooltipView | null = null;

  private ctx: CanvasRenderingContext2D | null = null;
  private dpr = 1;
  private w = 0;
  private h = 0;
  private animId = 0;
  private lastTime = 0;
  private effectImage: HTMLImageElement | null = null;
  private effectLoaded = false;
  private readonly avatars = new Map<number, HTMLImageElement>();
  private readonly hotspots: Hotspot[] = [
    { nx: 0.35, ny: 0.3 },
    { nx: 0.6, ny: 0.22 },
    { nx: 0.75, ny: 0.4 },
    { nx: 0.5, ny: 0.55 },
    { nx: 0.25, ny: 0.55 },
    { nx: 0.65, ny: 0.65 },
    { nx: 0.4, ny: 0.75 },
    { nx: 0.8, ny: 0.6 }
  ].map((pos, i) => ({
    ...pos,
    advocateIdx: i,
    expandProgress: 0,
    targetExpand: 0,
    label: null,
    labelOpacity: 0,
    labelOffsetY: 0
  }));

  private cycleTimer = 0;
  private hovered = -1;
  private resizeHandler = () => this.resize();
  private boundAnimate = (ts: number) => this.animate(ts);

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.loadImages();
    const canvas = this.canvasRef?.nativeElement;
    const container = this.containerRef?.nativeElement;
    if (!canvas || !container) {
      return;
    }
    this.ctx = canvas.getContext('2d');
    this.resize();
    window.addEventListener('resize', this.resizeHandler);
    this.ngZone.runOutsideAngular(() => {
      this.animId = requestAnimationFrame(this.boundAnimate);
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animId);
    window.removeEventListener('resize', this.resizeHandler);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hovered = -1;
    this.ngZone.run(() => (this.tooltip = null));
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) {
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let closest = -1;
    let closestD = 30;
    this.hotspots.forEach((s, i) => {
      const d = Math.hypot(s.nx * this.w - mx, s.ny * this.h - my);
      if (d < closestD) {
        closestD = d;
        closest = i;
      }
    });

    this.hovered = closest;

    const nextTooltip =
      closest >= 0
        ? {
            leftPx: this.hotspots[closest].nx * this.w,
            topPx: this.hotspots[closest].ny * this.h - 60,
            advocate: ADVOCATE_NAMES[this.hotspots[closest].advocateIdx]
          }
        : null;

    const prevKey = this.tooltipKey(this.tooltip);
    const nextKey = this.tooltipKey(nextTooltip);
    if (prevKey !== nextKey) {
      this.ngZone.run(() => (this.tooltip = nextTooltip));
    }
  }

  private tooltipKey(t: TooltipView | null): string {
    if (!t) {
      return '';
    }
    return `${t.advocate.name}-${t.leftPx}-${t.topPx}`;
  }

  private loadImages(): void {
    HEADSHOT_SRCS.forEach((src, i) => {
      const img = new Image();
      img.src = src;
      img.onload = () => this.avatars.set(i, img);
    });

    const effect = new Image();
    effect.src = ASSET('snoball-effect.png');
    effect.onload = () => {
      this.effectImage = effect;
      this.effectLoaded = true;
    };
  }

  private resize(): void {
    const canvas = this.canvasRef?.nativeElement;
    const container = this.containerRef?.nativeElement;
    if (!canvas || !container) {
      return;
    }
    const rect = container.getBoundingClientRect();
    this.dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * this.dpr;
    canvas.height = rect.height * this.dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    this.w = rect.width;
    this.h = rect.height;
  }

  private animate(ts: number): void {
    const ctx = this.ctx;
    if (!ctx) {
      this.animId = requestAnimationFrame(this.boundAnimate);
      return;
    }

    if (!this.lastTime) {
      this.lastTime = ts;
    }
    const dt = Math.min(ts - this.lastTime, 50);
    this.lastTime = ts;

    const { w, h } = this;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const img = this.effectImage;
    if (img && this.effectLoaded) {
      const imgAspect = img.width / img.height;
      const canvasAspect = w / h;
      let dw: number;
      let dh: number;
      let dx: number;
      let dy: number;
      if (canvasAspect > imgAspect) {
        dw = w;
        dh = w / imgAspect;
        dx = 0;
        dy = (h - dh) / 2;
      } else {
        dh = h;
        dw = h * imgAspect;
        dx = (w - dw) / 2;
        dy = 0;
      }
      ctx.globalAlpha = 0.6;
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.globalAlpha = 1;
    }

    this.cycleTimer += dt;
    const spots = this.hotspots;

    if (this.cycleTimer > CYCLE_MS) {
      this.cycleTimer = 0;
      const count = 2 + Math.floor(Math.random() * 3);
      const indices = new Set<number>();
      while (indices.size < count) {
        indices.add(Math.floor(Math.random() * spots.length));
      }
      spots.forEach((s, i) => {
        s.targetExpand = indices.has(i) ? 1 : 0;
        if (indices.has(i)) {
          s.label = SHARING_LABELS[Math.floor(Math.random() * SHARING_LABELS.length)];
          s.labelOpacity = 0;
          s.labelOffsetY = 0;
        }
      });
    }

    if (this.cycleTimer > FADE_START_MS) {
      spots.forEach(s => {
        s.targetExpand = 0;
      });
    }

    spots.forEach(s => {
      s.expandProgress += (s.targetExpand - s.expandProgress) * 0.06;
      if (s.targetExpand > 0 && s.label) {
        s.labelOpacity = Math.min(s.labelOpacity + dt * 0.002, 1);
        s.labelOffsetY -= dt * 0.008;
      } else {
        s.labelOpacity = Math.max(s.labelOpacity - dt * 0.003, 0);
      }
    });

    spots.forEach((s, i) => {
      const sx = s.nx * w;
      const sy = s.ny * h;
      const isHovered = this.hovered === i;
      const ep = s.expandProgress;

      if (ep < 0.02 && !isHovered) {
        return;
      }

      const showAmount = Math.max(ep, isHovered ? 0.7 : 0);
      const radius = 16 + showAmount * 8;

      if (showAmount > 0.1) {
        ctx.globalAlpha = showAmount * 0.15;
        const pulseR = radius * (1.5 + showAmount * 1.5);
        const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, pulseR);
        grad.addColorStop(0, AVATAR_COLORS[i % AVATAR_COLORS.length]);
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(sx, sy, pulseR, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = showAmount * 0.95;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(sx, sy, radius + 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = AVATAR_COLORS[i % AVATAR_COLORS.length];
      ctx.lineWidth = 2.5;
      ctx.globalAlpha = showAmount * 0.9;
      ctx.beginPath();
      ctx.arc(sx, sy, radius + 2, 0, Math.PI * 2);
      ctx.stroke();

      const avatar = this.avatars.get(s.advocateIdx);
      if (avatar) {
        ctx.globalAlpha = showAmount;
        ctx.save();
        ctx.beginPath();
        ctx.arc(sx, sy, radius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(avatar, sx - radius, sy - radius, radius * 2, radius * 2);
        ctx.restore();
      }

      if (ep > 0.3) {
        ctx.globalAlpha = (1 - ep) * 0.25;
        ctx.strokeStyle = AVATAR_COLORS[i % AVATAR_COLORS.length];
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(sx, sy, radius * (1.8 + ep * 0.8), 0, Math.PI * 2);
        ctx.stroke();
      }

      if (s.label && s.labelOpacity > 0.01) {
        ctx.globalAlpha = s.labelOpacity * 0.9;
        const lx = sx;
        const ly = sy + s.labelOffsetY - radius - 14;

        ctx.font = '500 11px omnesmedium, Omnes, sans-serif';
        const metrics = ctx.measureText(s.label);
        const pw = metrics.width + 16;
        const ph = 22;

        ctx.fillStyle = 'rgba(255,255,255,0.93)';
        this.fillRoundRect(ctx, lx - pw / 2, ly - ph / 2, pw, ph, ph / 2);
        ctx.fill();

        ctx.strokeStyle = 'rgba(54,55,149,0.12)';
        ctx.lineWidth = 0.5;
        this.strokeRoundRect(ctx, lx - pw / 2, ly - ph / 2, pw, ph, ph / 2);
        ctx.stroke();

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#363795';
        ctx.fillText(s.label, lx, ly + 0.5);
      }
    });

    ctx.globalAlpha = 1;
    this.animId = requestAnimationFrame(this.boundAnimate);
  }

  private fillRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ): void {
    ctx.beginPath();
    const rr = ctx as CanvasRenderingContext2D & { roundRect?: (...a: number[]) => void };
    if (typeof rr.roundRect === 'function') {
      rr.roundRect(x, y, w, h, r);
    } else {
      this.legacyRoundRectPath(ctx, x, y, w, h, r);
    }
  }

  private strokeRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ): void {
    ctx.beginPath();
    const rr = ctx as CanvasRenderingContext2D & { roundRect?: (...a: number[]) => void };
    if (typeof rr.roundRect === 'function') {
      rr.roundRect(x, y, w, h, r);
    } else {
      this.legacyRoundRectPath(ctx, x, y, w, h, r);
    }
  }

  private legacyRoundRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ): void {
    const rad = Math.min(r, w / 2, h / 2);
    ctx.moveTo(x + rad, y);
    ctx.arcTo(x + w, y, x + w, y + h, rad);
    ctx.arcTo(x + w, y + h, x, y + h, rad);
    ctx.arcTo(x, y + h, x, y, rad);
    ctx.arcTo(x, y, x + w, y, rad);
    ctx.closePath();
  }
}
